import React, { useState, useEffect, useRef, useCallback } from 'react';

const ExerciseImage = ({ exerciseId, resolution = 720, alt, className, style, lazy = true }) => {
  const [imageSrc, setImageSrc] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [currentResolution, setCurrentResolution] = useState(resolution);
  const [isInView, setIsInView] = useState(!lazy);
  const maxRetries = 2;
  const timeoutRef = useRef(null);
  const imageRef = useRef(null);
  const fallbackResolutions = [720, 360, 1080];

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, isInView]);

  useEffect(() => {
    if (!isInView) return;

    const fetchImage = async () => {
      try {
        setIsLoading(true);
        setError(false);
        
        // Add a small delay to prevent overwhelming the API
        const delay = Math.random() * 1000 + 500; // 500-1500ms random delay
        await new Promise(resolve => setTimeout(resolve, delay));
        
        const response = await fetch(
          `https://exercisedb.p.rapidapi.com/image?resolution=${currentResolution}&exerciseId=${exerciseId}`,
          {
            method: 'GET',
            headers: {
              'X-RapidAPI-Key': process.env.REACT_APP_RAPID_API_KEY,
              'X-RapidAPI-Host': 'exercisedb.p.rapidapi.com',
            },
          }
        );

        if (response.ok) {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.startsWith('image/')) {
            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            setImageSrc(imageUrl);
            setError(false);
          } else {
            throw new Error('Invalid content type');
          }
        } else if (response.status === 429 && retryCount < maxRetries) {
          // Rate limited, retry after delay
          const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff
          timeoutRef.current = setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, retryDelay);
          return;
        } else if (response.status === 404) {
          // Try different resolution if available
          const currentIndex = fallbackResolutions.indexOf(currentResolution);
          const nextIndex = (currentIndex + 1) % fallbackResolutions.length;
          if (nextIndex !== currentIndex && retryCount < maxRetries) {
            setCurrentResolution(fallbackResolutions[nextIndex]);
            setRetryCount(prev => prev + 1);
            return;
          }
          setError(true);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error(`Error fetching exercise image for ID ${exerciseId}:`, err);
        if (retryCount < maxRetries) {
          const retryDelay = Math.pow(2, retryCount) * 1000;
          timeoutRef.current = setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, retryDelay);
          return;
        }
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (exerciseId) {
      fetchImage();
    }

    // Cleanup blob URL and timeout on unmount
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [exerciseId, currentResolution, retryCount, isInView]);

  if (!isInView) {
    return (
      <div 
        ref={imageRef}
        className={className}
        style={{ 
          ...style, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f9f9f9',
          minHeight: '200px',
          color: '#999'
        }}
      >
        <div style={{ fontSize: '14px' }}>📷</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div 
        className={className}
        style={{ 
          ...style, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '200px',
          color: '#666'
        }}
      >
        <div style={{ fontSize: '14px' }}>Loading image...</div>
        {retryCount > 0 && (
          <div style={{ fontSize: '12px', marginTop: '5px' }}>
            Retry {retryCount}/{maxRetries}
          </div>
        )}
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e0e0e0',
          color: '#666',
          fontSize: '14px',
          textAlign: 'center',
          padding: '20px'
        }}
      >
        <div>
          <div>📷</div>
          <div style={{ marginTop: '5px' }}>
            {alt || 'Exercise'}
          </div>
          <div style={{ fontSize: '12px', marginTop: '3px' }}>
            Image unavailable
          </div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
    />
  );
};

export default ExerciseImage;